import { supabase } from '../lib/supabase'
import type { 
  ProjectTemplate,
  CreateProjectTemplateData,
  ProjectInstance
} from '../types/projectTemplate'

class ProjectTemplateService {
  // Project Template Methods
  
  async getProjectTemplates(userId: string, category?: ProjectTemplate['category'], includePublic: boolean = true): Promise<ProjectTemplate[]> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      let query = supabase
        .from('project_templates')
        .select(`
          *,
          phases:project_template_phases(*),
          tasks:project_template_tasks(*),
          deliverables:project_template_deliverables(*)
        `)

      if (includePublic) {
        query = query.or(`user_id.eq.${userId},is_public.eq.true,is_default.eq.true`)
      } else {
        query = query.eq('user_id', userId)
      }

      if (category) {
        query = query.eq('category', category)
      }

      query = query.order('is_default', { ascending: false })
        .order('usage_count', { ascending: false })
        .order('created_at', { ascending: false })

      const { data, error } = await query

      if (error) throw error

      return (data || []).map(template => ({
        id: template.id,
        userId: template.user_id,
        name: template.name,
        description: template.description,
        category: template.category,
        subcategory: template.subcategory,
        estimatedDuration: template.estimated_duration,
        recommendedTeamSize: template.recommended_team_size,
        difficultyLevel: template.difficulty_level,
        phases: (template.phases || []).map((phase: any) => ({
          id: phase.id,
          name: phase.name,
          description: phase.description,
          order: phase.order,
          estimatedDays: phase.estimated_days,
          color: phase.color,
          prerequisites: phase.prerequisites || [],
          deliverables: phase.deliverables || [],
          tasks: phase.tasks || []
        })),
        tasks: (template.tasks || []).map((task: any) => ({
          id: task.id,
          title: task.title,
          description: task.description,
          category: task.category,
          estimatedHours: task.estimated_hours,
          priority: task.priority,
          phaseId: task.phase_id,
          order: task.order,
          dependsOn: task.depends_on || [],
          assignedTo: task.assigned_to,
          skillsRequired: task.skills_required || [],
          daysBeforeShoot: task.days_before_shoot,
          daysAfterPrevious: task.days_after_previous,
          isAutomated: task.is_automated,
          automationTrigger: task.automation_trigger,
          resources: task.resources || [],
          notes: task.notes
        })),
        deliverables: (template.deliverables || []).map((deliverable: any) => ({
          id: deliverable.id,
          name: deliverable.name,
          description: deliverable.description,
          type: deliverable.type,
          format: deliverable.format,
          quality: deliverable.quality,
          quantity: deliverable.quantity,
          estimatedDeliveryDays: deliverable.estimated_delivery_days,
          phaseId: deliverable.phase_id,
          requiresClientApproval: deliverable.requires_client_approval,
          allowsClientRevisions: deliverable.allows_client_revisions,
          maxRevisions: deliverable.max_revisions,
          isIncludedInBase: deliverable.is_included_in_base,
          additionalCost: deliverable.additional_cost
        })),
        suggestedPricing: template.suggested_pricing,
        tags: template.tags || [],
        isPublic: template.is_public,
        isDefault: template.is_default,
        usageCount: template.usage_count,
        rating: template.rating,
        createdAt: template.created_at,
        updatedAt: template.updated_at
      }))
    } catch (error) {
      console.error('Error fetching project templates:', error)
      return []
    }
  }

  async getProjectTemplateById(templateId: string): Promise<ProjectTemplate | null> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const { data, error } = await supabase
        .from('project_templates')
        .select(`
          *,
          phases:project_template_phases(*),
          tasks:project_template_tasks(*),
          deliverables:project_template_deliverables(*)
        `)
        .eq('id', templateId)
        .single()

      if (error) throw error
      if (!data) return null

      return {
        id: data.id,
        userId: data.user_id,
        name: data.name,
        description: data.description,
        category: data.category,
        subcategory: data.subcategory,
        estimatedDuration: data.estimated_duration,
        recommendedTeamSize: data.recommended_team_size,
        difficultyLevel: data.difficulty_level,
        phases: (data.phases || []).map((phase: any) => ({
          id: phase.id,
          name: phase.name,
          description: phase.description,
          order: phase.order,
          estimatedDays: phase.estimated_days,
          color: phase.color,
          prerequisites: phase.prerequisites || [],
          deliverables: phase.deliverables || [],
          tasks: phase.tasks || []
        })),
        tasks: (data.tasks || []).map((task: any) => ({
          id: task.id,
          title: task.title,
          description: task.description,
          category: task.category,
          estimatedHours: task.estimated_hours,
          priority: task.priority,
          phaseId: task.phase_id,
          order: task.order,
          dependsOn: task.depends_on || [],
          assignedTo: task.assigned_to,
          skillsRequired: task.skills_required || [],
          daysBeforeShoot: task.days_before_shoot,
          daysAfterPrevious: task.days_after_previous,
          isAutomated: task.is_automated,
          automationTrigger: task.automation_trigger,
          resources: task.resources || [],
          notes: task.notes
        })),
        deliverables: (data.deliverables || []).map((deliverable: any) => ({
          id: deliverable.id,
          name: deliverable.name,
          description: deliverable.description,
          type: deliverable.type,
          format: deliverable.format,
          quality: deliverable.quality,
          quantity: deliverable.quantity,
          estimatedDeliveryDays: deliverable.estimated_delivery_days,
          phaseId: deliverable.phase_id,
          requiresClientApproval: deliverable.requires_client_approval,
          allowsClientRevisions: deliverable.allows_client_revisions,
          maxRevisions: deliverable.max_revisions,
          isIncludedInBase: deliverable.is_included_in_base,
          additionalCost: deliverable.additional_cost
        })),
        suggestedPricing: data.suggested_pricing,
        tags: data.tags || [],
        isPublic: data.is_public,
        isDefault: data.is_default,
        usageCount: data.usage_count,
        rating: data.rating,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      }
    } catch (error) {
      console.error('Error fetching project template:', error)
      return null
    }
  }

  async createProjectTemplate(userId: string, data: CreateProjectTemplateData): Promise<{ success: boolean; template?: ProjectTemplate; error?: string }> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      // Create the main template
      const templateData = {
        user_id: userId,
        name: data.name,
        description: data.description,
        category: data.category,
        subcategory: data.subcategory,
        estimated_duration: data.estimatedDuration,
        recommended_team_size: data.recommendedTeamSize,
        difficulty_level: data.difficultyLevel,
        suggested_pricing: data.suggestedPricing,
        tags: data.tags,
        is_public: data.isPublic,
        is_default: false,
        usage_count: 0
      }

      const { data: template, error: templateError } = await supabase
        .from('project_templates')
        .insert(templateData)
        .select()
        .single()

      if (templateError) throw templateError

      // Create phases
      if (data.phases.length > 0) {
        const phasesData = data.phases.map(phase => ({
          template_id: template.id,
          name: phase.name,
          description: phase.description,
          order: phase.order,
          estimated_days: phase.estimatedDays,
          color: phase.color,
          prerequisites: phase.prerequisites,
          deliverables: phase.deliverables,
          tasks: phase.tasks
        }))

        const { error: phasesError } = await supabase
          .from('project_template_phases')
          .insert(phasesData)

        if (phasesError) throw phasesError
      }

      // Create tasks
      if (data.tasks.length > 0) {
        const tasksData = data.tasks.map(task => ({
          template_id: template.id,
          title: task.title,
          description: task.description,
          category: task.category,
          estimated_hours: task.estimatedHours,
          priority: task.priority,
          phase_id: task.phaseId,
          order: task.order,
          depends_on: task.dependsOn,
          assigned_to: task.assignedTo,
          skills_required: task.skillsRequired,
          days_before_shoot: task.daysBeforeShoot,
          days_after_previous: task.daysAfterPrevious,
          is_automated: task.isAutomated,
          automation_trigger: task.automationTrigger,
          resources: task.resources,
          notes: task.notes
        }))

        const { error: tasksError } = await supabase
          .from('project_template_tasks')
          .insert(tasksData)

        if (tasksError) throw tasksError
      }

      // Create deliverables
      if (data.deliverables.length > 0) {
        const deliverablesData = data.deliverables.map(deliverable => ({
          template_id: template.id,
          name: deliverable.name,
          description: deliverable.description,
          type: deliverable.type,
          format: deliverable.format,
          quality: deliverable.quality,
          quantity: deliverable.quantity,
          estimated_delivery_days: deliverable.estimatedDeliveryDays,
          phase_id: deliverable.phaseId,
          requires_client_approval: deliverable.requiresClientApproval,
          allows_client_revisions: deliverable.allowsClientRevisions,
          max_revisions: deliverable.maxRevisions,
          is_included_in_base: deliverable.isIncludedInBase,
          additional_cost: deliverable.additionalCost
        }))

        const { error: deliverablesError } = await supabase
          .from('project_template_deliverables')
          .insert(deliverablesData)

        if (deliverablesError) throw deliverablesError
      }

      // Get the complete template
      const createdTemplate = await this.getProjectTemplateById(template.id)

      return {
        success: true,
        template: createdTemplate || undefined
      }
    } catch (error) {
      console.error('Error creating project template:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create project template'
      }
    }
  }

  async deleteProjectTemplate(templateId: string): Promise<{ success: boolean; error?: string }> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      // Check if template exists and user has permission
      const { data: template, error: fetchError } = await supabase
        .from('project_templates')
        .select('user_id, is_default')
        .eq('id', templateId)
        .single()

      if (fetchError) throw fetchError

      if (!template) {
        throw new Error('Template not found')
      }

      if (template.is_default) {
        throw new Error('Cannot delete default templates')
      }

      // Delete related data first (due to foreign key constraints)
      await supabase
        .from('project_template_deliverables')
        .delete()
        .eq('template_id', templateId)

      await supabase
        .from('project_template_tasks')
        .delete()
        .eq('template_id', templateId)

      await supabase
        .from('project_template_phases')
        .delete()
        .eq('template_id', templateId)

      // Delete the template
      const { error: deleteError } = await supabase
        .from('project_templates')
        .delete()
        .eq('id', templateId)

      if (deleteError) throw deleteError

      return { success: true }
    } catch (error) {
      console.error('Error deleting project template:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete project template'
      }
    }
  }

  async createProjectFromTemplate(userId: string, templateId: string, projectData: {
    name: string
    clientId?: string
    shootId?: string
    startDate: string
    shootDate?: string
  }): Promise<{ success: boolean; project?: ProjectInstance; error?: string }> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const template = await this.getProjectTemplateById(templateId)
      if (!template) {
        throw new Error('Template not found')
      }

      // Calculate expected end date
      const startDate = new Date(projectData.startDate)
      const expectedEndDate = new Date(startDate)
      expectedEndDate.setDate(startDate.getDate() + template.estimatedDuration)

      // Create project instance
      const projectInstanceData = {
        user_id: userId,
        template_id: templateId,
        client_id: projectData.clientId,
        shoot_id: projectData.shootId,
        name: projectData.name,
        status: 'planning',
        start_date: projectData.startDate,
        expected_end_date: expectedEndDate.toISOString().split('T')[0],
        shoot_date: projectData.shootDate
      }

      const { data: project, error: projectError } = await supabase
        .from('project_instances')
        .insert(projectInstanceData)
        .select()
        .single()

      if (projectError) throw projectError

      // Create project phases from template
      if (template.phases.length > 0) {
        const phasesData = template.phases.map(phase => ({
          project_id: project.id,
          template_phase_id: phase.id,
          name: phase.name,
          status: 'pending',
          progress: 0
        }))

        const { error: phasesError } = await supabase
          .from('project_instance_phases')
          .insert(phasesData)

        if (phasesError) throw phasesError
      }

      // Create project tasks from template
      if (template.tasks.length > 0) {
        const tasksData = template.tasks.map(task => {
          let dueDate: string | undefined

          if (task.daysBeforeShoot && projectData.shootDate) {
            const shootDate = new Date(projectData.shootDate)
            const taskDate = new Date(shootDate)
            taskDate.setDate(shootDate.getDate() - task.daysBeforeShoot)
            dueDate = taskDate.toISOString().split('T')[0]
          } else if (task.daysAfterPrevious) {
            const taskDate = new Date(startDate)
            taskDate.setDate(startDate.getDate() + task.daysAfterPrevious)
            dueDate = taskDate.toISOString().split('T')[0]
          }

          return {
            project_id: project.id,
            template_task_id: task.id,
            title: task.title,
            status: 'pending',
            due_date: dueDate
          }
        })

        const { error: tasksError } = await supabase
          .from('project_instance_tasks')
          .insert(tasksData)

        if (tasksError) throw tasksError
      }

      // Create project deliverables from template
      if (template.deliverables.length > 0) {
        const deliverablesData = template.deliverables.map(deliverable => ({
          project_id: project.id,
          template_deliverable_id: deliverable.id,
          name: deliverable.name,
          status: 'pending',
          revision_count: 0
        }))

        const { error: deliverablesError } = await supabase
          .from('project_instance_deliverables')
          .insert(deliverablesData)

        if (deliverablesError) throw deliverablesError
      }

      // Update template usage count
      await supabase
        .from('project_templates')
        .update({ usage_count: template.usageCount + 1 })
        .eq('id', templateId)

      // Get the complete project instance
      const createdProject = await this.getProjectInstance(project.id)

      return {
        success: true,
        project: createdProject || undefined
      }
    } catch (error) {
      console.error('Error creating project from template:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create project from template'
      }
    }
  }

  async getProjectInstances(userId: string, status?: ProjectInstance['status']): Promise<ProjectInstance[]> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      let query = supabase
        .from('project_instances')
        .select(`
          *,
          client:clients(*),
          template:project_templates(*),
          phases:project_instance_phases(*),
          tasks:project_instance_tasks(*),
          deliverables:project_instance_deliverables(*)
        `)
        .eq('user_id', userId)

      if (status) {
        query = query.eq('status', status)
      }

      query = query.order('created_at', { ascending: false })

      const { data, error } = await query

      if (error) throw error

      return (data || []).map(project => this.transformProjectInstance(project))
    } catch (error) {
      console.error('Error fetching project instances:', error)
      return []
    }
  }

  async getProjectInstance(projectId: string): Promise<ProjectInstance | null> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const { data, error } = await supabase
        .from('project_instances')
        .select(`
          *,
          phases:project_instance_phases(*),
          tasks:project_instance_tasks(*),
          deliverables:project_instance_deliverables(*),
          client:clients(id, name, email, company),
          template:project_templates(*)
        `)
        .eq('id', projectId)
        .single()

      if (error) throw error
      if (!data) return null

      return this.transformProjectInstance(data)
    } catch (error) {
      console.error('Error fetching project instance:', error)
      return null
    }
  }

  async updateProjectStatus(projectId: string, status: ProjectInstance['status']): Promise<{ success: boolean; error?: string }> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const { error } = await supabase
        .from('project_instances')
        .update({ 
          status,
          updated_at: new Date().toISOString(),
          // Set actual end date if completing the project
          ...(status === 'completed' && { actual_end_date: new Date().toISOString() })
        })
        .eq('id', projectId)

      if (error) throw error

      return { success: true }
    } catch (error) {
      console.error('Error updating project status:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  }

  async updateTaskStatus(taskId: string, status: 'pending' | 'in-progress' | 'completed' | 'cancelled'): Promise<{ success: boolean; error?: string }> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const { error } = await supabase
        .from('project_instance_tasks')
        .update({ 
          status,
          ...(status === 'completed' && { completed_date: new Date().toISOString() }),
          ...(status === 'in-progress' && { start_date: new Date().toISOString() })
        })
        .eq('id', taskId)

      if (error) throw error

      return { success: true }
    } catch (error) {
      console.error('Error updating task status:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  }

  async updateProject(projectId: string, updates: Partial<{
    name: string
    description: string
    startDate: string
    shootDate: string
    agreedPrice: number
  }>): Promise<{ success: boolean; error?: string }> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const dbUpdates: any = {
        updated_at: new Date().toISOString()
      }

      if (updates.name) dbUpdates.name = updates.name
      if (updates.description) dbUpdates.description = updates.description
      if (updates.startDate) dbUpdates.start_date = updates.startDate
      if (updates.shootDate) dbUpdates.shoot_date = updates.shootDate
      if (updates.agreedPrice !== undefined) dbUpdates.agreed_price = updates.agreedPrice

      const { error } = await supabase
        .from('project_instances')
        .update(dbUpdates)
        .eq('id', projectId)

      if (error) throw error

      return { success: true }
    } catch (error) {
      console.error('Error updating project:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  }

  private transformProjectInstance(data: any): ProjectInstance {
    return {
      id: data.id,
      userId: data.user_id,
      templateId: data.template_id,
      clientId: data.client_id,
      shootId: data.shoot_id,
      name: data.name,
      description: data.description,
      status: data.status,
      startDate: data.start_date,
      expectedEndDate: data.expected_end_date,
      actualEndDate: data.actual_end_date,
      shootDate: data.shoot_date,
      phases: (data.phases || []).map((phase: any) => ({
        id: phase.id,
        templatePhaseId: phase.template_phase_id,
        name: phase.name,
        status: phase.status,
        startDate: phase.start_date,
        endDate: phase.end_date,
        progress: phase.progress
      })),
      tasks: (data.tasks || []).map((task: any) => ({
        id: task.id,
        templateTaskId: task.template_task_id,
        title: task.title,
        status: task.status,
        assignedUserId: task.assigned_user_id,
        startDate: task.start_date,
        dueDate: task.due_date,
        completedDate: task.completed_date,
        actualHours: task.actual_hours,
        notes: task.notes
      })),
      deliverables: (data.deliverables || []).map((deliverable: any) => ({
        id: deliverable.id,
        templateDeliverableId: deliverable.template_deliverable_id,
        name: deliverable.name,
        status: deliverable.status,
        deliveredDate: deliverable.delivered_date,
        approvedDate: deliverable.approved_date,
        revisionCount: deliverable.revision_count,
        files: deliverable.files || []
      })),
      agreedPrice: data.agreed_price,
      paidAmount: data.paid_amount,
      client: data.client ? {
        id: data.client.id,
        name: data.client.name,
        email: data.client.email,
        company: data.client.company
      } : undefined,
      template: data.template ? {
        id: data.template.id,
        userId: data.template.user_id,
        name: data.template.name,
        description: data.template.description,
        category: data.template.category,
        subcategory: data.template.subcategory,
        estimatedDuration: data.template.estimated_duration,
        recommendedTeamSize: data.template.recommended_team_size,
        difficultyLevel: data.template.difficulty_level,
        phases: [],
        tasks: [],
        deliverables: [],
        suggestedPricing: data.template.suggested_pricing,
        tags: data.template.tags || [],
        isPublic: data.template.is_public,
        isDefault: data.template.is_default,
        usageCount: data.template.usage_count,
        rating: data.template.rating,
        createdAt: data.template.created_at,
        updatedAt: data.template.updated_at
      } : undefined,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    }
  }

  // Default templates that will be seeded
  async getDefaultTemplates(): Promise<CreateProjectTemplateData[]> {
    return [
      {
        name: 'Wedding Photography - Full Package',
        description: 'Complete wedding photography workflow from engagement to final delivery',
        category: 'wedding',
        subcategory: 'full-wedding',
        estimatedDuration: 30,
        recommendedTeamSize: 2,
        difficultyLevel: 'advanced',
        phases: [
          {
            name: 'Pre-Wedding',
            description: 'Planning and preparation phase',
            order: 1,
            estimatedDays: 14,
            color: '#3b82f6',
            prerequisites: [],
            deliverables: [],
            tasks: []
          },
          {
            name: 'Wedding Day',
            description: 'Ceremony and reception photography',
            order: 2,
            estimatedDays: 1,
            color: '#10b981',
            prerequisites: [],
            deliverables: [],
            tasks: []
          },
          {
            name: 'Post-Production',
            description: 'Editing and processing',
            order: 3,
            estimatedDays: 10,
            color: '#f59e0b',
            prerequisites: [],
            deliverables: [],
            tasks: []
          },
          {
            name: 'Delivery',
            description: 'Final delivery and client handoff',
            order: 4,
            estimatedDays: 5,
            color: '#8b5cf6',
            prerequisites: [],
            deliverables: [],
            tasks: []
          }
        ],
        tasks: [
          {
            title: 'Initial client consultation',
            description: 'Meet with couple to discuss vision and requirements',
            category: 'pre-production',
            estimatedHours: 2,
            priority: 'high',
            phaseId: '1',
            order: 1,
            daysBeforeShoot: 21,
            assignedTo: 'photographer'
          },
          {
            title: 'Location scouting',
            description: 'Visit ceremony and reception venues',
            category: 'pre-production',
            estimatedHours: 4,
            priority: 'medium',
            phaseId: '1',
            order: 2,
            daysBeforeShoot: 14,
            assignedTo: 'photographer'
          },
          {
            title: 'Equipment check and packing',
            description: 'Verify all equipment is ready and pack for wedding day',
            category: 'pre-production',
            estimatedHours: 2,
            priority: 'critical',
            phaseId: '1',
            order: 3,
            daysBeforeShoot: 1,
            assignedTo: 'photographer'
          }
        ],
        deliverables: [
          {
            name: 'Engagement Photos',
            description: 'Pre-wedding engagement session photos',
            type: 'photos',
            format: 'JPEG',
            quality: 'high-res',
            quantity: 50,
            estimatedDeliveryDays: 7,
            phaseId: '1',
            requiresClientApproval: true,
            allowsClientRevisions: true,
            maxRevisions: 2,
            isIncludedInBase: true
          },
          {
            name: 'Wedding Album',
            description: 'Professional wedding photo album',
            type: 'album',
            estimatedDeliveryDays: 21,
            phaseId: '4',
            requiresClientApproval: true,
            allowsClientRevisions: true,
            maxRevisions: 3,
            isIncludedInBase: true
          }
        ],
        suggestedPricing: {
          min: 2500,
          max: 8000,
          currency: 'USD'
        },
        tags: ['wedding', 'photography', 'full-service', 'premium'],
        isPublic: true
      },
      {
        name: 'Portrait Session - Individual',
        description: 'Professional individual portrait session workflow',
        category: 'portrait',
        subcategory: 'individual',
        estimatedDuration: 7,
        recommendedTeamSize: 1,
        difficultyLevel: 'beginner',
        phases: [
          {
            name: 'Planning',
            description: 'Session planning and preparation',
            order: 1,
            estimatedDays: 2,
            color: '#3b82f6',
            prerequisites: [],
            deliverables: [],
            tasks: []
          },
          {
            name: 'Photo Session',
            description: 'Portrait photography session',
            order: 2,
            estimatedDays: 1,
            color: '#10b981',
            prerequisites: [],
            deliverables: [],
            tasks: []
          },
          {
            name: 'Post-Production',
            description: 'Photo editing and retouching',
            order: 3,
            estimatedDays: 3,
            color: '#f59e0b',
            prerequisites: [],
            deliverables: [],
            tasks: []
          },
          {
            name: 'Delivery',
            description: 'Final photo delivery',
            order: 4,
            estimatedDays: 1,
            color: '#8b5cf6',
            prerequisites: [],
            deliverables: [],
            tasks: []
          }
        ],
        tasks: [
          {
            title: 'Client consultation call',
            description: 'Discuss styling, location, and expectations',
            category: 'pre-production',
            estimatedHours: 1,
            priority: 'high',
            phaseId: '1',
            order: 1,
            daysBeforeShoot: 3,
            assignedTo: 'photographer'
          },
          {
            title: 'Location setup',
            description: 'Prepare lighting and backdrop',
            category: 'production',
            estimatedHours: 1,
            priority: 'medium',
            phaseId: '2',
            order: 1,
            daysBeforeShoot: 0,
            assignedTo: 'photographer'
          }
        ],
        deliverables: [
          {
            name: 'Edited Portrait Gallery',
            description: 'High-resolution edited portraits',
            type: 'digital-gallery',
            format: 'JPEG',
            quality: 'high-res',
            quantity: 15,
            estimatedDeliveryDays: 5,
            phaseId: '4',
            requiresClientApproval: false,
            allowsClientRevisions: true,
            maxRevisions: 1,
            isIncludedInBase: true
          }
        ],
        suggestedPricing: {
          min: 200,
          max: 800,
          currency: 'USD'
        },
        tags: ['portrait', 'individual', 'studio', 'professional'],
        isPublic: true
      }
    ]
  }
}

export const projectTemplateService = new ProjectTemplateService()